import React, {useState, useEffect} from 'react'
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Divider from '@mui/material/Divider';
import ListItemText from '@mui/material/ListItemText';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import Avatar from '@mui/material/Avatar';
import Typography from '@mui/material/Typography';
import { Fragment } from 'react';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import { setcurrentPair, setPairType } from '../Trader/tradingSlice';
import { useNavigate } from 'react-router-dom';
import { Paper, Alert } from '@mui/material';

const StockAssetsList = () => {
    const formatPrice = (amount)=> new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', currencySign: 'standard' }).format(amount);
    
    // Use the correct Redux path for stocks data
    const stocks = useSelector((state) => state.coins.stock);
    
    const [coinSymbol, setCoinSymbol] = useState('');
    
    const defaultStocks = [
      { short_name: 'AAPL', name: 'Apple Inc.', priceUsd: 185.92, change_percentage: '0.75%' },
      { short_name: 'MSFT', name: 'Microsoft Corporation', priceUsd: 417.80, change_percentage: '1.25%' },
      { short_name: 'AMZN', name: 'Amazon.com Inc.', priceUsd: 179.62, change_percentage: '-0.50%' },
      { short_name: 'GOOGL', name: 'Alphabet Inc.', priceUsd: 172.45, change_percentage: '0.90%' }
    ];

    // Function to get stock logo URL using logo.dev service
    const getStockLogoUrl = (symbol) => {
      return `https://img.logo.dev/ticker/${symbol}?token=pk_Pb2cclXnTuuh7Lsj1Ys7ew&retina=true`;
    };

    const fundings = useSelector((state) => state.wallet.fundings);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const tradings = useSelector((state) => state.history.tradings);
    const investments = useSelector((state) => state.history.investments);
    const withdrawals = useSelector((state) => state.history.withdrawals);

    const calculate24hrChange = (coin) => {
      let rate = 0;
      if (Array.isArray(stocks)) {
        stocks.forEach(stock => {
          if (stock.short_name === coin) {
            rate = parseFloat(stock.change_percentage || 0);
          }
        });
      }
      return rate;
    }

    const calculateBalance = (coin) => {
      let amount = 0;

      if (Array.isArray(tradings)) {
        tradings.forEach(trade => {
          if (trade.pair === coin) {
            amount += parseFloat(trade.profit);
          }
        });
      }
      
      return amount;
    }

    // Check if there are any stock assets with balance
    const hasAssetsWithBalance = Array.isArray(stocks) && stocks.some(stock => 
      calculateBalance(stock.short_name) > 0
    );
    
  return (
    <div>
        {!Array.isArray(stocks) || stocks.length < 1 ? (
          <Paper elevation={0} sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 1, textAlign: 'center' }}>
            <CircularProgress size={30} sx={{ mb: 2 }}/>
            <Typography variant="body2" color="text.secondary">
              Loading stock data...
            </Typography>
          </Paper>
        ) : (
          <List sx={{ width: '100%', bgcolor: 'transparent' }}>
            {!hasAssetsWithBalance && (
              <Paper elevation={0} sx={{ mb: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
                <Alert severity="info" sx={{ mb: 2 }}>
                  You don't have any stock assets with balance. Here are some popular stocks you can trade.
                </Alert>
                
                {defaultStocks.map((stock) => (
                  <Fragment key={stock.short_name}>
                    <ListItem alignItems="flex-start" sx={{cursor: 'pointer'}}
                      onClick={()=>{
                        dispatch(setcurrentPair(stock.short_name));
                        dispatch(setPairType('stocks'));
                        setTimeout(() => {
                          navigate('/trading-page');
                        }, 100);
                      }}
                    >
                      <ListItemAvatar>
                        <Avatar 
                          alt={stock.name}
                          src={getStockLogoUrl(stock.short_name)}
                          sx={{ 
                            width: 40,
                            height: 40
                          }}
                        >
                          {stock.short_name.substring(0, 2)}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={stock.short_name}
                        secondary={
                          <React.Fragment>
                            <Typography
                              sx={{ display: 'inline' }}
                              component="span"
                              variant="body2"
                              color="text.primary"
                            >
                              {stock.name}
                            </Typography>
                          </React.Fragment>
                        }
                      />

                      <Box edge="end" sx={{display: 'flex', flexDirection: 'column', justifyContent: 'space-between', alignContent: 'flex-start', alignItems: 'flex-start'}}>
                        <Typography mx={1}>
                          {formatPrice(stock.priceUsd)}
                        </Typography>

                        <Typography mx={1} sx={{display: 'flex', justifyContent: 'space-between'}}>
                          <Typography style={{fontWeight: 'lighter', color: `${stock.change_percentage.startsWith('+') ? '#13e576' : '#e8073c'}`}} variant="caption" display="block" gutterBottom>
                            {stock.change_percentage}
                          </Typography>
                          <Typography style={{marginLeft: '2px'}} variant="caption" display="block" gutterBottom>
                            24hr
                          </Typography> 
                        </Typography>
                      </Box>

                      <Box edge="end" sx={{display: 'flex', flexDirection: 'column', justifyContent: 'space-between', alignContent: 'flex-start', alignItems: 'flex-start'}}>
                        <Typography mx={1} sx={{fontWeight: 'bolder'}}>
                          {formatPrice(0)}
                        </Typography>

                        <Typography mx={1} variant="caption">
                          0.00
                        </Typography>
                      </Box>
                    </ListItem>
                    <Divider variant="inset" component="li" />
                  </Fragment>
                ))}
              </Paper>
            )}

            {stocks.map((stock) => {
              const balance = calculateBalance(stock.short_name);
              return balance > 0 && (
                <Fragment key={stock.short_name}>
                  <ListItem alignItems="flex-start" sx={{cursor: 'pointer'}}
                    onClick={()=>{
                      dispatch(setcurrentPair(stock.short_name));
                      dispatch(setPairType('stocks'));
                      setTimeout(() => {
                        navigate('/trading-page');
                      }, 100);
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar 
                        alt={stock.name || stock.short_name}
                        src={getStockLogoUrl(stock.short_name)}
                        sx={{ 
                          width: 40,
                          height: 40
                        }}
                      >
                        {stock.short_name?.substring(0, 2) || 'ST'}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={stock.short_name || 'Unknown'}
                      secondary={
                        <React.Fragment>
                          <Typography
                            sx={{ display: 'inline' }}
                            component="span"
                            variant="body2"
                            color="text.primary"
                          >
                            {stock.name || 'Unknown Stock'}
                          </Typography>
                        </React.Fragment>
                      }
                    />

                    <Box edge="end" sx={{display: 'flex', flexDirection: 'column', justifyContent: 'space-between', alignContent: 'flex-start', alignItems: 'flex-start'}}>
                      <Typography mx={1}>
                        {formatPrice(stock.priceUsd || 0)}
                      </Typography>

                      <Typography mx={1} sx={{display: 'flex', justifyContent: 'space-between'}}>
                        <Typography style={{fontWeight: 'lighter', color: `${calculate24hrChange(stock.short_name) > 0 ? '#13e576' : '#e8073c'}`}} variant="caption" display="block" gutterBottom>
                            {calculate24hrChange(stock.short_name).toFixed(2)}%
                        </Typography>
                        <Typography style={{marginLeft: '2px'}} variant="caption" display="block" gutterBottom>
                            24hr
                        </Typography> 
                      </Typography>
                    </Box>

                    <Box edge="end" sx={{display: 'flex', flexDirection: 'column', justifyContent: 'space-between', alignContent: 'flex-start', alignItems: 'flex-start'}}>
                      <Typography mx={1} sx={{fontWeight: 'bolder'}}>
                        {formatPrice(balance)}
                      </Typography>

                      <Typography mx={1} variant="caption">
                        {((stock.priceUsd && balance/stock.priceUsd) || 0).toFixed(8)}
                      </Typography>
                    </Box>
                  </ListItem>
                  <Divider variant="inset" component="li" />
                </Fragment>
              );
            })}
          </List>
        )}
    </div>
  )
}

export default StockAssetsList
